import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { 
  ArrowLeft,
  Package, 
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  User,
  MapPin,
  CreditCard,
  Loader2,
  Save,
  Send
} from 'lucide-react';
import { toast } from 'sonner';

const AdminOrderDetail = () => {
  const { orderId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchOrder();
  }, [orderId, user]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/admin/orders/${orderId}`);
      setOrder(response.data);
      setSelectedStatus(response.data.status);
      setTrackingNumber(response.data.tracking_number || '');
    } catch (error) {
      console.error('Failed to fetch order:', error);
      toast.error('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const updateOrder = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${orderId}`, {
        status: selectedStatus,
        tracking_number: trackingNumber || null
      });
      toast.success('Order updated successfully');
      fetchOrder();
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
      case 'processing': return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
      case 'shipped': return 'text-purple-400 bg-purple-400/10 border-purple-400/30';
      case 'delivered': return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'cancelled': return 'text-red-400 bg-red-400/10 border-red-400/30';
      default: return 'text-zinc-400 bg-zinc-400/10 border-zinc-400/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5" />;
      case 'processing': return <Package className="w-5 h-5" />;
      case 'shipped': return <Truck className="w-5 h-5" />;
      case 'delivered': return <CheckCircle className="w-5 h-5" />;
      case 'cancelled': return <XCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090D0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#090D0F] flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Order Not Found</h2>
          <Link to="/admin">
            <Button className="mt-4">Back to Admin</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090D0F]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/admin" size="default" />
            <span className="text-xs text-teal-400 font-medium">ADMIN</span>
          </div>
          <Link to="/admin">
            <Button variant="ghost" className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Orders
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Order Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Order #{order.id.slice(0, 8).toUpperCase()}</h1>
            <p className="text-zinc-500">
              Created on {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString()}
            </p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-sm border ${getStatusColor(order.status)}`}>
            {getStatusIcon(order.status)}
            <span className="font-medium capitalize">{order.status}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Items */}
            <div className="bg-[#121416] border border-zinc-800 rounded-sm">
              <div className="p-6 border-b border-zinc-800">
                <h2 className="text-lg font-bold">Order Items</h2>
              </div>
              <div className="divide-y divide-zinc-800">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="p-6 flex items-center gap-4">
                    <div className="w-16 h-16 bg-zinc-800 rounded-sm overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1638805981949-362f5964521e?q=85&w=100"
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-zinc-500">Quantity: {item.quantity}</p>
                    </div>
                    <p className="font-bold">${(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="p-6 bg-zinc-800/30 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Subtotal</span>
                  <span>${order.subtotal?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Tax</span>
                  <span>${order.tax?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Shipping</span>
                  <span>{order.shipping === 0 ? 'FREE' : `$${order.shipping}`}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-zinc-700">
                  <span>Total</span>
                  <span className="text-teal-400">${order.total?.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-teal-400" />
                <h2 className="text-lg font-bold">Shipping Address</h2>
              </div>
              <div className="text-zinc-400">
                <p className="text-white font-medium">
                  {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                </p>
                <p>{order.shipping_address?.address_line1}</p>
                {order.shipping_address?.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                <p>
                  {order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}
                </p>
                <p>{order.shipping_address?.country}</p>
                {order.shipping_address?.phone && (
                  <p className="mt-2">Phone: {order.shipping_address.phone}</p>
                )}
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-teal-400" />
                <h2 className="text-lg font-bold">Payment Information</h2>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Payment Method</span>
                  <span className="capitalize">{order.payment_method}</span>
                </div>
                {order.stripe_session_id && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Stripe Session</span>
                    <span className="font-mono text-xs">{order.stripe_session_id.slice(0, 20)}...</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Update Order */}
          <div className="space-y-6">
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-bold mb-6">Update Order</h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400">Status</Label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full mt-1 bg-[#090D0F] border border-zinc-700 rounded-sm px-4 py-2 text-white"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <Label className="text-zinc-400">Tracking Number</Label>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number"
                    className="mt-1 bg-[#090D0F] border-zinc-700"
                  />
                </div>

                <Button
                  onClick={updateOrder}
                  disabled={saving}
                  className="w-full bg-teal-400 text-black font-bold hover:bg-teal-300"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-bold mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Button variant="outline" className="w-full border-zinc-700 justify-start">
                  <Send className="w-4 h-4 mr-2" />
                  Send Shipping Update
                </Button>
                <Button variant="outline" className="w-full border-zinc-700 justify-start">
                  <User className="w-4 h-4 mr-2" />
                  Contact Customer
                </Button>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-[#121416] border border-zinc-800 rounded-sm p-6">
              <h2 className="text-lg font-bold mb-4">Order Timeline</h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-teal-400 mt-2" />
                  <div>
                    <p className="text-sm font-medium">Order Created</p>
                    <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleString()}</p>
                  </div>
                </div>
                {order.updated_at !== order.created_at && (
                  <div className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-400 mt-2" />
                    <div>
                      <p className="text-sm font-medium">Last Updated</p>
                      <p className="text-xs text-zinc-500">{new Date(order.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminOrderDetail;
